import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useExpert } from '../context/ExpertContext';
import { 
  Users, FileCheck, FileX, Clock, Activity, CheckCircle2, XCircle,
  Shield, LogOut, User, ChevronRight, ExternalLink, Beaker,
  BarChart3, TrendingUp, AlertTriangle, RefreshCw, Eye, History,
  Search, Filter, ArrowRight, ArrowDown, ArrowUp, Bell, BookOpen, Zap
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { expert, token, logout, isSuperAdmin, loading: authLoading } = useExpert();
  
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [historyChanges, setHistoryChanges] = useState([]);
  const [selectedChange, setSelectedChange] = useState(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Guideline monitoring state
  const [guidelineNotifications, setGuidelineNotifications] = useState([]);
  const [guidelineStatus, setGuidelineStatus] = useState(null);
  const [checkingGuidelines, setCheckingGuidelines] = useState(false);
  const [selectedGuideline, setSelectedGuideline] = useState(null);
  const [showGuidelineDialog, setShowGuidelineDialog] = useState(false);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          navigate('/expert');
          return;
        }
        throw new Error('Failed to fetch dashboard');
      }
      
      const data = await response.json();
      setDashboardData(data);
      setAuditLogs(data.recent_activity || []);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  // Fetch pending change requests
  const fetchPendingChanges = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/change-requests?status=pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      setPendingChanges(data.change_requests || []);
    } catch (err) {
      console.error('Failed to fetch pending changes:', err);
    }
  }, [token]);

  // Fetch history (approved/rejected) change requests
  const fetchHistoryChanges = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/change-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      const history = (data.change_requests || []).filter(c => c.status !== 'pending');
      setHistoryChanges(history);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  }, [token]);

  // Fetch guideline notifications
  const fetchGuidelineNotifications = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/guideline-notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      setGuidelineNotifications(data.notifications || []);
    } catch (err) {
      console.error('Failed to fetch guideline notifications:', err);
    }
  }, [token]);

  // Fetch guideline monitoring status
  const fetchGuidelineStatus = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/guideline-monitoring-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      setGuidelineStatus(data);
    } catch (err) {
      console.error('Failed to fetch guideline status:', err);
    }
  }, [token]);

  // Check for new guidelines
  const handleCheckGuidelines = async () => {
    setCheckingGuidelines(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/check-guidelines`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      alert(`Guideline check completed!\nDiseases checked: ${data.diseases_checked}\nNew notifications: ${data.notifications_created}`);
      fetchGuidelineNotifications();
      fetchGuidelineStatus();
    } catch (err) {
      console.error('Failed to check guidelines:', err);
      alert('Failed to check guidelines');
    } finally {
      setCheckingGuidelines(false);
    }
  };

  // Review guideline notification
  const handleReviewGuideline = async (action) => {
    if (!selectedGuideline) return;
    setProcessing(true);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/guideline-notifications/${selectedGuideline.id}/review?action=${action}&notes=${encodeURIComponent(reviewNotes)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!response.ok) throw new Error('Failed to review');
      
      setShowGuidelineDialog(false);
      setSelectedGuideline(null);
      setReviewNotes('');
      fetchGuidelineNotifications();
      fetchGuidelineStatus();
    } catch (err) {
      console.error('Failed to review guideline:', err);
      alert('Failed to review guideline notification');
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    // Wait for auth to load before checking
    if (authLoading) return;
    
    if (!token) {
      navigate('/expert-login');
      return;
    }
    fetchDashboard();
    fetchPendingChanges();
    fetchHistoryChanges();
    fetchGuidelineNotifications();
    fetchGuidelineStatus();
  }, [token, authLoading, navigate, fetchDashboard, fetchPendingChanges, fetchHistoryChanges, fetchGuidelineNotifications, fetchGuidelineStatus]);

  const handleReviewChange = (change) => {
    setSelectedChange(change);
    setReviewNotes('');
    setShowReviewDialog(true);
  };

  const handleViewHistory = (change) => {
    setSelectedChange(change);
    setShowHistoryDialog(true);
  };

  const handleApprove = async () => {
    if (!selectedChange) return;
    setProcessing(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/change-requests/${selectedChange.id}/approve?reviewer_notes=${encodeURIComponent(reviewNotes)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to approve');
      
      setShowReviewDialog(false);
      setSelectedChange(null);
      fetchPendingChanges();
      fetchHistoryChanges();
      fetchDashboard();
    } catch (err) {
      console.error('Failed to approve:', err);
      alert('Failed to approve change request');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedChange || !reviewNotes) {
      alert('Please provide a reason for rejection');
      return;
    }
    setProcessing(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/change-requests/${selectedChange.id}/reject?reviewer_notes=${encodeURIComponent(reviewNotes)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to reject');
      
      setShowReviewDialog(false);
      setSelectedChange(null);
      fetchPendingChanges();
      fetchHistoryChanges();
      fetchDashboard();
    } catch (err) {
      console.error('Failed to reject:', err);
      alert('Failed to reject change request');
    } finally {
      setProcessing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/expert-login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const diseaseProgress = dashboardData?.diseases 
    ? (dashboardData.diseases.reviewed / dashboardData.diseases.total) * 100 
    : 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/50 sticky top-0 z-50 backdrop-blur">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Shield className="w-6 h-6 text-primary" />
            <h1 className="text-xl font-bold">PhiCor Admin Dashboard</h1>
            <Badge variant={isSuperAdmin ? 'default' : 'secondary'}>
              {isSuperAdmin ? 'Super Admin' : 'Admin'}
            </Badge>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/expert')}>
              <Eye className="w-4 h-4 mr-2" />
              Expert View
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/expert/experiments')}>
              <Beaker className="w-4 h-4 mr-2" />
              Experiments
            </Button>
            <div className="flex items-center gap-2 px-3 py-1 bg-muted rounded-md">
              <User className="w-4 h-4" />
              <span className="text-sm font-medium">{expert?.name}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Experts</p>
                  <p className="text-2xl font-bold">{dashboardData?.experts?.total || 0}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Diseases Reviewed</p>
                  <p className="text-2xl font-bold text-green-500">
                    {dashboardData?.diseases?.reviewed || 0}/{dashboardData?.diseases?.total || 0}
                  </p>
                </div>
                <FileCheck className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-orange-500/50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Changes</p>
                  <p className="text-2xl font-bold text-orange-500">
                    {dashboardData?.change_requests?.pending || 0}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Approved Changes</p>
                  <p className="text-2xl font-bold text-green-500">
                    {dashboardData?.change_requests?.approved || 0}
                  </p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Experiments</p>
                  <p className="text-2xl font-bold">{dashboardData?.experiments?.total || 0}</p>
                </div>
                <Beaker className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Card */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Disease Curation Progress</span>
              <span className="text-sm text-muted-foreground">{diseaseProgress.toFixed(1)}%</span>
            </div>
            <Progress value={diseaseProgress} className="h-3" />
            <p className="text-xs text-muted-foreground mt-2">
              {dashboardData?.diseases?.pending_review || 0} diseases pending expert review
            </p>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Change Requests with Tabs */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-primary" />
                    Change Requests
                  </CardTitle>
                  <CardDescription>Review, approve, or reject expert submissions</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-orange-500/10 text-orange-500">
                    {pendingChanges.length} pending
                  </Badge>
                  <Badge variant="secondary" className="bg-green-500/10 text-green-500">
                    {historyChanges.filter(c => c.status === 'approved').length} approved
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-4">
                  <TabsTrigger value="pending" className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Pending ({pendingChanges.length})
                  </TabsTrigger>
                  <TabsTrigger value="history" className="flex items-center gap-2">
                    <History className="w-4 h-4" />
                    History ({historyChanges.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="pending">
                  <ScrollArea className="h-[350px]">
                    {pendingChanges.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No pending change requests</p>
                        <p className="text-sm">All caught up! 🎉</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {pendingChanges.map(change => (
                          <Card key={change.id} className="cursor-pointer hover:border-orange-500/50 border-l-4 border-l-orange-500" onClick={() => handleReviewChange(change)}>
                            <CardContent className="pt-4">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="font-medium">{change.disease_name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {change.organ_id} / {change.scale_id}
                                  </p>
                                  <div className="flex items-center gap-2 mt-2">
                                    <Badge variant="outline">{change.expert_specialty}</Badge>
                                    <span className="text-xs text-muted-foreground">
                                      by {change.expert_name}
                                    </span>
                                  </div>
                                </div>
                                <Button size="sm" variant="outline">
                                  Review
                                  <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                              </div>
                              
                              <div className="mt-3 p-2 bg-muted/50 rounded text-sm">
                                {change.parameters.map((p, i) => (
                                  <div key={i} className="flex items-center gap-2">
                                    <span className="font-mono text-xs bg-background px-1 rounded">{p.parameter_name}</span>
                                    <span className="text-red-500 line-through">{p.current_value}</span>
                                    <ArrowRight className="w-3 h-3" />
                                    <span className="text-green-500 font-medium">{p.proposed_value}</span>
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="history">
                  {/* Search and Filter */}
                  <div className="flex gap-2 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input 
                        placeholder="Search by disease or expert..." 
                        className="pl-9"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-[130px]">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <ScrollArea className="h-[300px]">
                    {historyChanges
                      .filter(c => {
                        const matchesSearch = searchTerm === '' || 
                          c.disease_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          c.expert_name?.toLowerCase().includes(searchTerm.toLowerCase());
                        const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
                        return matchesSearch && matchesStatus;
                      })
                      .length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No history found</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {historyChanges
                          .filter(c => {
                            const matchesSearch = searchTerm === '' || 
                              c.disease_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              c.expert_name?.toLowerCase().includes(searchTerm.toLowerCase());
                            const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
                            return matchesSearch && matchesStatus;
                          })
                          .map(change => (
                          <Card 
                            key={change.id} 
                            className={`cursor-pointer hover:border-primary/50 border-l-4 ${
                              change.status === 'approved' ? 'border-l-green-500' : 'border-l-red-500'
                            }`}
                            onClick={() => handleViewHistory(change)}
                          >
                            <CardContent className="py-3">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    {change.status === 'approved' ? (
                                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                                    ) : (
                                      <XCircle className="w-4 h-4 text-red-500" />
                                    )}
                                    <span className="font-medium text-sm">{change.disease_name}</span>
                                    <Badge variant={change.status === 'approved' ? 'default' : 'destructive'} className="text-xs">
                                      {change.status}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Submitted by {change.expert_name} • Reviewed by {change.reviewer_name || 'N/A'}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {change.reviewed_at ? new Date(change.reviewed_at).toLocaleString() : 'N/A'}
                                  </p>
                                </div>
                                <Eye className="w-4 h-4 text-muted-foreground" />
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Expert Activity & Audit Logs */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Activity</CardTitle>
              <CardDescription>Latest actions in the system</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {auditLogs.slice(0, 20).map((log, index) => (
                    <div key={log.id || index} className="flex items-start gap-3 pb-3 border-b last:border-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        log.action === 'change_approved' ? 'bg-green-500/10 text-green-500' :
                        log.action === 'change_rejected' ? 'bg-red-500/10 text-red-500' :
                        log.action === 'change_requested' ? 'bg-blue-500/10 text-blue-500' :
                        log.action === 'disease_reviewed' ? 'bg-purple-500/10 text-purple-500' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {log.action === 'change_approved' ? <CheckCircle2 className="w-4 h-4" /> :
                         log.action === 'change_rejected' ? <XCircle className="w-4 h-4" /> :
                         log.action === 'change_requested' ? <FileCheck className="w-4 h-4" /> :
                         <Activity className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{log.expert_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {log.action.replace(/_/g, ' ')} - {log.target_name}
                        </p>
                        <p className="text-xs text-muted-foreground/70">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Guideline Monitoring Card */}
        <Card className="mt-6 border-blue-500/30">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-500" />
                  AI Guideline Monitoring
                </CardTitle>
                <CardDescription>Track updates to medical guidelines and protocols</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {guidelineStatus?.pending_notifications > 0 && (
                  <Badge variant="destructive" className="animate-pulse">
                    {guidelineStatus.pending_notifications} new
                  </Badge>
                )}
                {isSuperAdmin && (
                  <Button 
                    size="sm" 
                    onClick={handleCheckGuidelines}
                    disabled={checkingGuidelines}
                  >
                    {checkingGuidelines ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Zap className="w-4 h-4 mr-2" />
                    )}
                    Check Now
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Status Overview */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="p-3 bg-orange-500/10 rounded-lg text-center">
                <p className="text-2xl font-bold text-orange-500">{guidelineStatus?.pending_notifications || 0}</p>
                <p className="text-xs text-muted-foreground">Pending Review</p>
              </div>
              <div className="p-3 bg-green-500/10 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-500">{guidelineStatus?.reviewed_notifications || 0}</p>
                <p className="text-xs text-muted-foreground">Reviewed</p>
              </div>
              <div className="p-3 bg-muted rounded-lg text-center">
                <p className="text-2xl font-bold">{guidelineStatus?.dismissed_notifications || 0}</p>
                <p className="text-xs text-muted-foreground">Dismissed</p>
              </div>
            </div>

            {/* Last Check Info */}
            {guidelineStatus?.last_check && (
              <p className="text-xs text-muted-foreground mb-4">
                Last checked: {new Date(guidelineStatus.last_check).toLocaleString()}
              </p>
            )}

            {/* Notifications List */}
            <ScrollArea className="h-[200px]">
              {guidelineNotifications.filter(n => n.status === 'pending').length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Bell className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No pending guideline updates</p>
                  <p className="text-xs">Click &quot;Check Now&quot; to scan for new guidelines</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {guidelineNotifications.filter(n => n.status === 'pending').map(notification => (
                    <Card 
                      key={notification.id} 
                      className="cursor-pointer hover:border-blue-500/50 border-l-4 border-l-blue-500"
                      onClick={() => {
                        setSelectedGuideline(notification);
                        setReviewNotes('');
                        setShowGuidelineDialog(true);
                      }}
                    >
                      <CardContent className="py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Bell className="w-4 h-4 text-blue-500" />
                              <span className="font-medium text-sm">{notification.disease_name}</span>
                              <Badge variant="outline" className="text-xs">
                                {notification.guideline_source}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {notification.summary}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(notification.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Experts List */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Registered Experts
            </CardTitle>
            <CardDescription>All experts in the curation system</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboardData?.experts?.list?.map(exp => (
                <Card key={exp.id}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{exp.name}</p>
                        <p className="text-sm text-muted-foreground">{exp.specialty}</p>
                        <p className="text-xs text-muted-foreground">{exp.institution}</p>
                      </div>
                      <Badge variant={
                        exp.role === 'super_admin' ? 'default' :
                        exp.role === 'admin' ? 'secondary' : 'outline'
                      }>
                        {exp.role}
                      </Badge>
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Reviewed: </span>
                        <span className="font-medium">{exp.diseases_reviewed || 0}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Changes: </span>
                        <span className="font-medium">{exp.pending_changes || 0}</span>
                      </div>
                    </div>
                    {exp.last_login && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Last active: {new Date(exp.last_login).toLocaleDateString()}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review Change Request</DialogTitle>
            <DialogDescription>
              Review the proposed changes and approve or reject
            </DialogDescription>
          </DialogHeader>
          
          {selectedChange && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Disease</p>
                  <p className="font-medium">{selectedChange.disease_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Submitted by</p>
                  <p className="font-medium">{selectedChange.expert_name}</p>
                  <p className="text-xs text-muted-foreground">{selectedChange.expert_specialty}</p>
                </div>
              </div>
              
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium mb-3">Proposed Changes</p>
                {selectedChange.parameters.map((param, i) => (
                  <div key={i} className="mb-4 last:mb-0 p-3 bg-background rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-mono text-sm font-bold">{param.parameter_name}</span>
                      <Badge variant="outline" className="text-xs">
                        {selectedChange.organ_id} / {selectedChange.scale_id}
                      </Badge>
                    </div>
                    
                    {/* Visual Diff */}
                    <div className="flex items-center gap-3 p-2 bg-muted/50 rounded mb-2">
                      <div className="flex-1 p-2 bg-red-500/10 rounded border border-red-500/20">
                        <p className="text-xs text-red-500 mb-1">Current Value</p>
                        <p className="font-mono text-sm line-through text-red-400">{param.current_value}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 p-2 bg-green-500/10 rounded border border-green-500/20">
                        <p className="text-xs text-green-500 mb-1">Proposed Value</p>
                        <p className="font-mono text-sm font-bold text-green-500">{param.proposed_value}</p>
                      </div>
                    </div>
                    
                    {param.source_url && (
                      <div className="mt-2">
                        <a 
                          href={param.source_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-blue-500 hover:underline flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          View Evidence Source
                        </a>
                      </div>
                    )}
                    
                    {param.reasoning && (
                      <div className="mt-2 p-2 bg-background rounded text-sm">
                        <p className="text-muted-foreground text-xs mb-1">Reasoning:</p>
                        <p>{param.reasoning}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="review-notes">Review Notes</Label>
                <Textarea
                  id="review-notes"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes about your decision (required for rejection)..."
                  rows={3}
                />
              </div>
              
              <Alert>
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription>
                  Approving will apply these changes to the main database. This action will be logged.
                </AlertDescription>
              </Alert>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={processing}>
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
            <Button onClick={handleApprove} disabled={processing}>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Approve & Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History View Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedChange?.status === 'approved' ? (
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
              Change Request - {selectedChange?.status?.toUpperCase()}
            </DialogTitle>
            <DialogDescription>
              Viewing historical change request details
            </DialogDescription>
          </DialogHeader>
          
          {selectedChange && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Disease</p>
                  <p className="font-medium">{selectedChange.disease_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Submitted by</p>
                  <p className="font-medium">{selectedChange.expert_name}</p>
                  <p className="text-xs text-muted-foreground">{selectedChange.expert_specialty}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Reviewed by</p>
                  <p className="font-medium">{selectedChange.reviewer_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Reviewed at</p>
                  <p className="font-medium">
                    {selectedChange.reviewed_at 
                      ? new Date(selectedChange.reviewed_at).toLocaleString() 
                      : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Diff View */}
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium mb-3">Changes {selectedChange.status === 'approved' ? 'Applied' : 'Proposed'}</p>
                {selectedChange.parameters?.map((param, i) => (
                  <div key={i} className="mb-4 last:mb-0 p-3 bg-background rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-mono text-sm font-bold">{param.parameter_name}</span>
                      <Badge variant="outline" className="text-xs">
                        {selectedChange.organ_id} / {selectedChange.scale_id}
                      </Badge>
                    </div>
                    
                    {/* Visual Diff */}
                    <div className="flex items-center gap-3 p-2 bg-muted/50 rounded">
                      <div className="flex-1 p-2 bg-red-500/10 rounded border border-red-500/20">
                        <p className="text-xs text-red-500 mb-1">Original</p>
                        <p className="font-mono text-sm line-through text-red-400">{param.current_value}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 p-2 bg-green-500/10 rounded border border-green-500/20">
                        <p className="text-xs text-green-500 mb-1">
                          {selectedChange.status === 'approved' ? 'Applied' : 'Proposed'}
                        </p>
                        <p className="font-mono text-sm font-bold text-green-500">{param.proposed_value}</p>
                      </div>
                    </div>
                    
                    {param.source_url && (
                      <div className="mt-2">
                        <a 
                          href={param.source_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-blue-500 hover:underline flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          View Evidence Source
                        </a>
                      </div>
                    )}
                    
                    {param.reasoning && (
                      <div className="mt-2 p-2 bg-muted rounded text-sm">
                        <p className="text-muted-foreground text-xs mb-1">Reasoning:</p>
                        <p>{param.reasoning}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Reviewer Notes */}
              {selectedChange.reviewer_notes && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium mb-1">Reviewer Notes</p>
                  <p className="text-sm text-muted-foreground">{selectedChange.reviewer_notes}</p>
                </div>
              )}

              {selectedChange.status === 'rejected' && (
                <Alert variant="destructive">
                  <XCircle className="w-4 h-4" />
                  <AlertDescription>
                    This change request was rejected. The proposed changes were not applied to the database.
                  </AlertDescription>
                </Alert>
              )}

              {selectedChange.status === 'approved' && (
                <Alert>
                  <CheckCircle2 className="w-4 h-4" />
                  <AlertDescription>
                    This change was approved and applied to the main database.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guideline Review Dialog */}
      <Dialog open={showGuidelineDialog} onOpenChange={setShowGuidelineDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-500" />
              Guideline Update Alert
            </DialogTitle>
            <DialogDescription>
              Review this potential guideline update
            </DialogDescription>
          </DialogHeader>
          
          {selectedGuideline && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline">{selectedGuideline.guideline_source}</Badge>
                  <Badge variant="secondary">{new Date(selectedGuideline.created_at).toLocaleDateString()}</Badge>
                </div>
                <p className="font-medium text-lg">{selectedGuideline.disease_name}</p>
              </div>

              <div>
                <Label className="text-muted-foreground">Summary</Label>
                <p className="text-sm mt-1">{selectedGuideline.summary}</p>
              </div>

              {selectedGuideline.details && (
                <div>
                  <Label className="text-muted-foreground">Details</Label>
                  <p className="text-sm mt-1">{selectedGuideline.details}</p>
                </div>
              )}

              {selectedGuideline.url && (
                <a 
                  href={selectedGuideline.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-blue-500 hover:underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Source Document
                </a>
              )}

              <div className="space-y-2">
                <Label>Review Notes (Optional)</Label>
                <Textarea 
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes about your review decision..."
                  rows={2}
                />
              </div>

              <Alert>
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription>
                  If this guideline update is relevant, consider creating a change request in the Expert Dashboard to update the disease parameters accordingly.
                </AlertDescription>
              </Alert>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGuidelineDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="secondary" 
              onClick={() => handleReviewGuideline('dismissed')} 
              disabled={processing}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Dismiss
            </Button>
            <Button 
              onClick={() => handleReviewGuideline('reviewed')} 
              disabled={processing}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Mark Reviewed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
